import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Button, Modal, ModalBody } from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import {
  useGetTenantUsers,
  useSearchTenantUsers,
  useDeleteTenantUser,
} from "../../api/users/service/index";
import { useNavigate } from "react-router-dom";
import { getActiveColorStyle } from "../../utils/colorUtils";
import UsersFilters from "./components/UsersFilters";
import UsersCards from "./components/UsersCards";
import {
  readListState,
  saveListState,
  useClearListStateOnLeave,
  LIST_STATE_FLOWS,
} from "utils/listStateSessionStorage";

const USERS_LIST_STATE_KEY = "dealertiq-users-list-state";

const User = ({ activeColor = "secondary", account, shellThemeVariant = "1" }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useClearListStateOnLeave(USERS_LIST_STATE_KEY, LIST_STATE_FLOWS.users);
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const storedStateRef = useRef(readListState(USERS_LIST_STATE_KEY));
  const [page, setPage] = useState(() => storedStateRef.current?.page ?? 1);
  const [pageSize, setPageSize] = useState(() => storedStateRef.current?.pageSize ?? 20);
  const [searchInput, setSearchInput] = useState(() => storedStateRef.current?.searchInput ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => (storedStateRef.current?.searchInput ?? "").trim());
  const searchTimer = useRef(null);
  const searchHydratedRef = useRef(false);
  const listStateHydratedRef = useRef(false);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      if (!searchHydratedRef.current) {
        searchHydratedRef.current = true;
      } else {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  useEffect(() => {
    if (!listStateHydratedRef.current) {
      listStateHydratedRef.current = true;
      return;
    }

    saveListState(USERS_LIST_STATE_KEY, {
      searchInput,
      page,
      pageSize,
    });
  }, [searchInput, page, pageSize]);

  const isSearching = debouncedSearch.length >= 2;

  const { data: listData, isLoading: listLoading } = useGetTenantUsers(page, pageSize, !isSearching);
  const { data: searchData, isFetching: searchFetching } = useSearchTenantUsers(
    debouncedSearch,
    isSearching
  );

  const data = isSearching ? searchData : listData;
  const isLoading = isSearching ? searchFetching : listLoading;

  const serverPageCount = data?.pagination?.pageCount;
  useEffect(() => {
    if (isSearching) return;
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page, isSearching]);

  const isTheme2 = shellThemeVariant === "2";
  const UsersTable = isTheme2 ? ReactTable2 : ReactTable;

  const deleteUserMutation = useDeleteTenantUser();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteUser, setSelectedDeleteUser] = useState(null);

  const usersData = useMemo(
    () =>
      (data?.results || []).map((user) => ({
        id: user?.id || "-",
        documentId: user?.documentId || "-",
        fullName: user.user_detail?.full_name || "-",
        email: user.email || "-",
        phone: user.user_detail?.work_phone || "-",
        active: user.confirmed ? t("usersList.yes") : t("usersList.no"),
      })),
    [data?.results, t]
  );

  const handleDeleteClick = useCallback((user) => {
    setSelectedDeleteUser(user);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (selectedDeleteUser) {
      deleteUserMutation.mutate(selectedDeleteUser.documentId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteUser(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteUser(null);
  };

  const userColumns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: t("usersList.columns.name"), accessor: "fullName" },
      { Header: t("usersList.columns.email"), accessor: "email" },
      { Header: t("usersList.columns.phone"), accessor: "phone" },
      { Header: t("usersList.columns.active"), accessor: "active" },
      {
        id: "actions",
        Header: t("usersList.columns.actions"),
        accessor: "documentId",
        disableSortBy: true,
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="actions-right">
              {account.permissions.users_edit && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/users/${r.id}`);
                  }}
                >
                  <i className="tim-icons icon-pencil" />
                </Button>
              )}
              {account.permissions.users_delete && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(r);
                  }}
                  disabled={deleteUserMutation.isPending}
                >
                  {deleteUserMutation.isPending && selectedDeleteUser?.id === r.id ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <i className="tim-icons icon-simple-remove" />
                  )}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      account.permissions.users_edit,
      account.permissions.users_delete,
      navigate,
      handleDeleteClick,
      deleteUserMutation.isPending,
      selectedDeleteUser?.id,
    ]
  );

  const usersNoDataText = isSearching
    ? "No users found for your search."
    : t("searchDeals.noRecordsFound");

  const headerActions = account.permissions.users_add ? (
    <Button
      size="sm"
      style={{ ...activeColorStyle, color: "white" }}
      onClick={() => navigate("/users/add")}
    >
      {t("usersList.addUser")}
    </Button>
  ) : null;

  const filters = (
    <UsersFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      searchInput={searchInput}
      onSearchChange={setSearchInput}
    />
  );

  const tableProps = {
    activeColor,
    data: usersData,
    columns: userColumns,
    manualPagination: !isSearching,
    pageCount: isSearching ? 1 : data?.pagination?.pageCount ?? 1,
    pageIndex: page - 1,
    totalItems: !isSearching ? data?.pagination?.total : undefined,
    pageSize,
    noDataText: usersNoDataText,
    loading: isLoading,
    loadingText: t("searchDeals.loading"),
    onPageChange: (newPageIndex) => setPage(newPageIndex + 1),
    onPageSizeChange: (newSize) => {
      setPageSize(newSize);
      setPage(1);
    },
    onRowClick: (rowData) => {
      if (account.permissions.users_edit) {
        navigate(`/users/${rowData.id}`);
      }
    },
  };

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: isTheme2 ? "13px" : undefined, minWidth: 0, maxWidth: "100%" }}
    >
      {isTheme2 ? (
        <UsersTable
          {...tableProps}
          className="-striped -highlight sticky-stock-table"
        />
      ) : (
        <UsersTable
          {...tableProps}
          showPaginationTop
          showPaginationBottom={false}
          className="-striped -highlight"
        />
      )}
    </div>
  );

  return (
    <>
      <div
        className={classNames("content", "search-users", {
          "search-users--theme2": isTheme2,
          "theme2-admin-content-compact": isTheme2,
          "theme2-users-inset": isTheme2,
        })}
      >
        <UsersCards
          isTheme2={isTheme2}
          filters={filters}
          table={table}
          headerActions={headerActions}
        />
      </div>

      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t("usersList.modal.title")}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t("usersList.modal.confirm", { name: selectedDeleteUser?.fullName || "" })}</p>
          <p className="text-muted">{t("usersList.modal.cannotUndo")}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteUserMutation.isPending}
          >
            {t("usersList.modal.cancel")}
          </Button>
          <Button
            style={{
              background: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                />
                {t("usersList.modal.deleting")}
              </>
            ) : (
              t("usersList.modal.yesDelete")
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default User;
